import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { API_ENDPOINTS } from '../../config/api';
import { formatTimeWithZone, getTimeInMinutesInZone } from '../utils/timezone';

interface TimeSlot {
  start: Date;
  end: Date;
  available: boolean;
}

interface AppointmentTakerInfo {
  email: string;
  userId: string;
  timeZone?: string;
}

interface TimeSlotPickerProps {
  date: Date;
  serviceId: string;
  onSlotSelect: (slot: TimeSlot) => void;
  /** Optional minimum time - slots starting before this will be filtered out */
  minTime?: Date;
  /** Optional custom empty message when no slots are available */
  emptyMessage?: string;
  /** Optional appointment taker info - if provided, will use this instead of fetching */
  appointmentTaker?: AppointmentTakerInfo;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  date,
  serviceId,
  onSlotSelect,
  minTime,
  emptyMessage,
  appointmentTaker: appointmentTakerProp,
}) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [appointmentTakerTimeZone, setAppointmentTakerTimeZone] = useState<string>(
    appointmentTakerProp?.timeZone || 'America/Toronto'
  ); // Default to EST or prop value


  useEffect(() => {
    if (date && serviceId) {
      fetchAvailableSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, serviceId, minTime?.getTime(), appointmentTakerProp?.userId]);

  // Update timezone when appointmentTakerProp changes
  useEffect(() => {
    if (appointmentTakerProp?.timeZone) {
      setAppointmentTakerTimeZone(appointmentTakerProp.timeZone);
    }
  }, [appointmentTakerProp?.timeZone]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    setError(null);
    setSelectedSlot(null); // Reset selection when date/minTime changes

    try {
      // First, fetch unavailability data directly from availability API
      const dateStr = format(date, 'yyyy-MM-dd');
      const startDate = dateStr;
      const endDate = dateStr;
      
      // Use appointment taker from prop if provided, otherwise fetch from API
      let appointmentOwners: string[] = [];
      let firstTaker: any = null;

      if (appointmentTakerProp) {
        // Use the provided appointment taker
        appointmentOwners = [appointmentTakerProp.email];
        firstTaker = {
          userId: appointmentTakerProp.userId,
          email: appointmentTakerProp.email,
          timeZone: appointmentTakerProp.timeZone
        };
      } else {
        // Fallback: Fetch appointment takers enabled for booking app from API
        appointmentOwners = []; // Will be populated from API
        try {
          const baseUrl = API_ENDPOINTS.SLOTS.replace('/appointment/slots', '');
          const takersResponse = await fetch(`${baseUrl}/appointment/takers/booking`);
          if (takersResponse.ok) {
            const takersData = await takersResponse.json();
            if (takersData.success && takersData.appointmentTakers) {
              appointmentOwners = takersData.appointmentTakers.map((taker: any) => taker.email);
              if (takersData.appointmentTakers.length > 0) {
                firstTaker = takersData.appointmentTakers[0];
                if (firstTaker.timeZone) {
                  setAppointmentTakerTimeZone(firstTaker.timeZone);
                }
              }
            }
          }
        } catch (error) {
          // Silently fall back to default
        }
      }
      
      const unavailabilityPromises = appointmentOwners.map(async (ownerEmail) => {
        try {
          const unavailabilityUrl = `${API_ENDPOINTS.AVAILABILITY}?startDate=${startDate}&endDate=${endDate}&appointmentForEmail=${ownerEmail}`;
          const response = await fetch(unavailabilityUrl);
          if (response.ok) {
            const data = await response.json();
            return { ownerEmail, data };
          }
          return { ownerEmail, data: null, error: response.statusText };
        } catch (err) {
          return { ownerEmail, data: null, error: err instanceof Error ? err.message : 'Unknown error' };
        }
      });
      
      const unavailabilityResults = await Promise.all(unavailabilityPromises);
      
      const unavailabilityDetails = unavailabilityResults.map(r => ({
        owner: r.ownerEmail,
        hasData: !!r.data,
        success: r.data?.success,
        availabilityCount: r.data?.availability?.length || 0,
        availability: r.data?.availability || [],
        error: r.error,
        fullResponse: r.data
      }));
      
      // Now fetch available slots - use the appointment taker's userId or email
      let slotsUrl = `${API_ENDPOINTS.SLOTS}?date=${date.toISOString()}&serviceId=${serviceId}`;
      
      // If we have appointment taker info, pass it to the slots API
      if (firstTaker) {
        if (firstTaker.userId) {
          slotsUrl += `&appointmentForUserId=${firstTaker.userId}`;
        } else if (firstTaker.email) {
          slotsUrl += `&appointmentForEmail=${firstTaker.email}`;
        }
      } else if (appointmentOwners.length > 0) {
        // Fallback to email if we don't have the full taker object
        slotsUrl += `&appointmentForEmail=${appointmentOwners[0]}`;
      }
      
      const response = await fetch(slotsUrl);

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        const errorMessage = errorPayload.message || 'Failed to fetch available slots';
        
        setError(errorMessage.includes('Cannot get availability for past dates') ? 'Not Available' : errorMessage);
        setTimeSlots([]);
        return;
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.availableSlots)) {
        // Extract blocked unavailability records for the appointment owner
        const blockedRecords = unavailabilityDetails
          .flatMap(result => result.availability || [])
          .filter((rec: any) => 
            rec.isAvailable === false && 
            appointmentOwners.includes(rec.appointmentOwner)
          );
        
        // Helper function to get time in minutes from a time string (e.g., "15:30" -> 930)
        const timeStringToMinutes = (timeStr: string): number => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        };
        
        // Helper function to get time in minutes from a UTC date in appointment taker's timezone
        const getTimeInMinutesInTakerZone = (utcDate: Date): number => {
          return getTimeInMinutesInZone(utcDate, appointmentTakerTimeZone);
        };
        
        // Helper function to check if a slot overlaps with a blocked period
        const slotOverlapsWithBlock = (slotStartUTC: Date, slotEndUTC: Date, blockRecord: any): boolean => {
          // If no start/end time, it's a full day block - block all slots
          if (!blockRecord.startTime || !blockRecord.endTime || blockRecord.startTime === '' || blockRecord.endTime === '') {
            return true;
          }
          
          // Get slot times in minutes (appointment taker's timezone)
          const slotStartMinutes = getTimeInMinutesInTakerZone(slotStartUTC);
          const slotEndMinutes = getTimeInMinutesInTakerZone(slotEndUTC);
          
          // Get blocked times in minutes
          const blockStartMinutes = timeStringToMinutes(blockRecord.startTime);
          const blockEndMinutes = timeStringToMinutes(blockRecord.endTime);
          
          // Check if slot overlaps with blocked period
          // Slot overlaps if: slot starts before block ends AND slot ends after block starts
          return slotStartMinutes < blockEndMinutes && slotEndMinutes > blockStartMinutes;
        };
        
        // Count available vs unavailable slots
        let availableCount = 0;
        let unavailableCount = 0;
        
        const normalizedSlots = data.availableSlots
          .map((slot: any) => {
            const rawStart = slot?.start ?? slot?.startTime;
            const rawEnd = slot?.end ?? slot?.endTime;
            const start = rawStart ? new Date(rawStart) : null;
            const end = rawEnd ? new Date(rawEnd) : null;

            if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
              return null;
            }

            // Filter out slots that start before minTime
            if (minTime && start < minTime) {
              return null;
            }

            // Start with availability from appointment controller
            let isAvailable = Boolean(slot.available ?? slot.isAvailable ?? true);
            
            // Check against unavailability records
            if (isAvailable && blockedRecords.length > 0) {
              // Get date components for slot (in UTC - slots from backend are UTC)
              const slotYear = start.getUTCFullYear();
              const slotMonth = start.getUTCMonth();
              const slotDay = start.getUTCDate();
              
              const overlapsWithAnyBlock = blockedRecords.some((blockRecord: any) => {
                // Parse the blocked date - it's stored as ISO string
                const blockDateObj = new Date(blockRecord.date);
                // Get UTC date components
                const blockYear = blockDateObj.getUTCFullYear();
                const blockMonth = blockDateObj.getUTCMonth();
                const blockDay = blockDateObj.getUTCDate();
                
                // Compare dates (year, month, day in UTC)
                const dateMatch = 
                  slotYear === blockYear &&
                  slotMonth === blockMonth &&
                  slotDay === blockDay;
                
                if (dateMatch) {
                  return slotOverlapsWithBlock(start, end, blockRecord);
                }
                return false;
              });
              
              if (overlapsWithAnyBlock) {
                isAvailable = false;
              }
            }
            
            if (isAvailable) {
              availableCount++;
            } else {
              unavailableCount++;
            }

            return {
              start,
              end,
              available: isAvailable,
            } as TimeSlot;
          })
          .filter((slot: TimeSlot | null): slot is TimeSlot => slot !== null);

        setTimeSlots(normalizedSlots);
      } else {
        const errorMessage = data.message || 'Failed to fetch available slots';
        setError(errorMessage.includes('Cannot get availability for past dates') ? 'Not Available' : errorMessage);
        setTimeSlots([]);
      }
    } catch (error) {
      setError('Failed to load available time slots');
      setTimeSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotClick = (slot: TimeSlot) => {
    if (slot.available) {
      // Update internal state first for immediate UI feedback
      setSelectedSlot(slot);
      // Then notify parent component
      onSlotSelect(slot);
    }
  };

  const formatTime = (date: Date) => {
    return formatTimeWithZone(date, 'h:mm a', appointmentTakerTimeZone);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Available Slots for {format(date, 'EEEE MMM d, yyyy')}
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {error && (
            <Paper sx={{ p: 2, mt: 2, textAlign: 'center', bgcolor: 'background.default' }}>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Paper>
          )}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
        gap: 1,
              mt: 2,
            }}
          >
        {timeSlots.map((slot, index) => {
              const isSelected =
                selectedSlot &&
                selectedSlot.start.getTime() === slot.start.getTime() &&
                selectedSlot.end.getTime() === slot.end.getTime();
          
          return (
              <Button
                  key={`${slot.start.toISOString()}-${index}`}
                variant="outlined"
                onClick={() => handleSlotClick(slot)}
                disabled={!slot.available}
                title={slot.available ? 'Available' : 'Unavailable - Blocked'}
                sx={{ 
                    flex: '1 1 calc(50% - 8px)',
                    minWidth: '120px',
                    maxWidth: '160px',
                  minHeight: '48px',
                    borderColor: slot.available ? '#b8b8b8' : '#999999',
                    borderWidth: slot.available ? 1 : 2,
                    borderStyle: slot.available ? 'solid' : 'dashed',
                    color: isSelected ? '#1f1f1f' : slot.available ? '#0f0f0f' : '#666666',
                    backgroundColor: isSelected ? '#e0e0e0' : slot.available ? '#ffffff' : '#f5f5f5',
                    opacity: slot.available ? 1 : 0.7,
                    cursor: slot.available ? 'pointer' : 'not-allowed',
                    '&:hover': slot.available
                      ? {
                          backgroundColor: '#f1f1f1',
                          borderColor: '#aaaaaa',
                        }
                      : {
                          backgroundColor: '#f5f5f5',
                          borderColor: '#999999',
                        },
                    '&:disabled': {
                      borderColor: '#999999',
                      color: '#666666',
                      opacity: 0.7,
                    },
                  '&:focus-visible': {
                    outline: 'none',
                      boxShadow: slot.available ? '0 0 0 2px rgba(80, 80, 80, 0.35)' : '0 0 0 2px rgba(153, 153, 153, 0.35)',
                  },
                }}
              >
                {formatTime(slot.start)} - {formatTime(slot.end)}
              </Button>
          );
        })}
      </Box>
      
          {timeSlots.filter(s => s.available).length === 0 && !error && timeSlots.length > 0 && (
            <Paper sx={{ p: 2, mt: 2, textAlign: 'center', bgcolor: 'background.default' }}>
          <Typography color="text.secondary">
            All time slots are blocked for this date. Please select another date.
          </Typography>
        </Paper>
          )}
          
          {timeSlots.length === 0 && !error && (
            <Paper sx={{ p: 2, mt: 2, textAlign: 'center', bgcolor: 'background.default' }}>
          <Typography color="text.secondary">
            {emptyMessage || 'No available time slots for this date. Please select another date.'}
          </Typography>
        </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default TimeSlotPicker; 